import { useApolloClient } from '@apollo/client'
import * as Sentry from '@sentry/nextjs'
import { useInterpret } from '@xstate/react'
import { FirebaseError } from 'firebase/app'
import {
  AuthError,
  fetchSignInMethodsForEmail,
  getAdditionalUserInfo,
  getAuth,
  getRedirectResult,
  isSignInWithEmailLink,
  linkWithCredential,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
  signOut
} from 'firebase/auth'
import { useRouter } from 'next/router'
import React, { createContext } from 'react'
import { deleteAuthCookies } from 'api/authCookies'
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from 'utils/LocalStorage'
import { config } from 'utils/config'
import { sleep } from 'utils/sleep'
import { useReturnToPath } from 'utils/useReturnToPath'
import { useUiContext } from 'modules/UiContext'
import { useUpdateProfile } from 'modules/member/settings/queries/useProfile'
import { deleteKnockCookie } from 'modules/notifications/NotificationsProvider'
import { AuthService, authenticationMachine, AuthContext } from '../machines/authenticationMachine'
import { mapProviderDataToProfileInput } from '../machines/mapProviderDataToProfileInput'
import { PROVIDERS } from '../useAuthentication'
import { useSessionContext } from './SessionProvider'

const EMAIL_MEMO_KEY = 'signinEmail'

const getTrackingPayload = (ctx?: AuthContext, payload: Record<string, any> = {}) => {
  return {
    modalOpen: ctx?.modalOpen,
    mode: ctx?.mode,
    copyContext: ctx?.copyContext,
    providerId: ctx?.provider?.providerId,
    analyticsContext: ctx?.analyticsContext,
    email: ctx?.email,
    ...payload
  }
}

const trackAuthEvent = (action: string, ctx?: AuthContext, payload: Record<string, any> = {}) => {
  const trackingPayload = getTrackingPayload(ctx, payload)

  window.analytics.track('Auth', { action, ...trackingPayload })
}

const logException = (error: Error, email?: string) => {
  Sentry.withScope((scope) => {
    scope.setLevel(Sentry.Severity.Error)
    if (email) {
      scope.setUser({ email })
    }
    Sentry.captureException(error)
  })
}

interface Context {
  authenticationService: AuthService
}

export const AuthenticationContext = createContext<Context>({
  authenticationService: null
})

interface Props {
  children: React.ReactNode
}
export const AuthenticationProvider = ({ children }: Props) => {
  const { push } = useRouter()
  const { addNotification } = useUiContext()
  const { userIsLoaded } = useSessionContext()
  const { updateProfile } = useUpdateProfile()
  const { returnToOrRedirect } = useReturnToPath()
  const client = useApolloClient()

  const authenticationService = useInterpret(authenticationMachine, {
    devTools: config.apiEnv !== 'production',
    actions: {
      trackModalState: (ctx) => {
        trackAuthEvent('modalState', ctx)
      },
      logUnexpectedFailureFromMachine: (_, event) => {
        const { data: error } = event as any

        Sentry.withScope((scope) => {
          scope.setLevel(Sentry.Severity.Error)
          scope.setTag('authError', event.type)
          Sentry.captureException(error)
        })
      }
    },
    services: {
      /**
       * Get the initial login state from firebase. If the user is logged in, the promise resolves, otherwise it rejects.
       */
      getLoginState: () => {
        return new Promise((resolve) => {
          const auth = getAuth()
          /**
           * For the initial state of the machine, we want to wait for auth to initialize, and then
           * transition based on whether you're signed in or not. So this takes advantage of the onAuthStateChanged
           * callback which doesn't fire until auth is loaded, but then immediately unsubscribes to cleanup.
           */
          const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            unsubscribe()
            resolve({ isSignedIn: !!firebaseUser })
          })
        })
      },

      /**
       * Initiate a social signin provider via popup
       */
      signInWithProvider: async (ctx) => {
        trackAuthEvent('signInWithProviderStarted', ctx)
        const { provider } = ctx
        const isEmbeddedSocialBrowser = /(tiktok)|(instagram)|(facebook)/gi.test(window.navigator.userAgent)
        const auth = getAuth()

        try {
          if (isEmbeddedSocialBrowser) {
            /**
             * Note that this will not work in incognito mode, but since it's an embedded browser we know this is not the case.
             */
            await signInWithRedirect(auth, provider)
          } else {
            const result = await signInWithPopup(auth, provider)
            const additionalUserInfo = getAdditionalUserInfo(result)

            if (!additionalUserInfo.profile.email) {
              throw new FirebaseError('auth/missing-email', 'This account has no associated email')
            }

            trackAuthEvent('signInWithProviderSuccess', ctx, { isNewUser: additionalUserInfo.isNewUser })
            return additionalUserInfo
          }
        } catch (err) {
          trackAuthEvent('signInWithProviderError', ctx, { errorCode: err.code })
          const { code } = err as AuthError

          switch (code) {
            case 'auth/missing-email':
              throw new Error('This provider could not be used for your account. Please use a different sign-in method.')

            case 'auth/popup-blocked':
              throw new Error('The popup was blocked by the browser')

            case 'auth/account-exists-with-different-credential':
              throw err

            case 'auth/popup-closed-by-user':
              throw new Error('') // Throwing an empty error will make it not show in the UI

            default:
              logException(err, ctx?.email)
              throw new Error('Something went wrong. If the problem persists, please contact support@thingtesting.com')
          }
        }
      },

      /**
       * Attempt to get additional data from a redirect result
       */
      getRedirectResult: async (ctx) => {
        try {
          const auth = getAuth()
          const result = await getRedirectResult(auth)

          if (result?.user) {
            const additionalUserInfo = getAdditionalUserInfo(result)
            const mergedAdditionalUserInfo = { ...ctx.additionalUserInfo, ...additionalUserInfo }
            trackAuthEvent(
              'signInWithProviderSuccess',
              {
                ...ctx,
                analyticsContext: 'redirect'
              },
              { isNewUser: additionalUserInfo?.isNewUser, redirect: true }
            )
            return mergedAdditionalUserInfo
          } else {
            return ctx.additionalUserInfo
          }
        } catch (err) {
          logException(err, ctx?.email)
          throw new Error('Something went wrong. If the problem persists, please contact support@thingtesting.com')
        }
      },

      signInWithExistingProvider: async (ctx) => {
        trackAuthEvent('signInWithExistingProviderStarted', ctx)
        const { pendingCredential, provider } = ctx
        try {
          const auth = getAuth()
          const result = await signInWithPopup(auth, provider)
          const additionalUserInfo = getAdditionalUserInfo(result)
          await linkWithCredential(result.user, pendingCredential)
          trackAuthEvent('signInWithExistingProviderSuccess', ctx, { isNewUser: additionalUserInfo.isNewUser })
          return additionalUserInfo
        } catch (err) {
          trackAuthEvent('signInWithExistingProviderError', ctx, { errorCode: err.code })
          logException(err, ctx?.email)
          throw new Error('Something went wrong. If the problem persists, please contact support@thingtesting.com')
        }
      },

      getSigninProvidersForEmail: async ({ email }) => {
        const auth = getAuth()
        const signinMethods = await fetchSignInMethodsForEmail(auth, email)
        const provider = Object.values(PROVIDERS).find((provider) => signinMethods.some((id) => id === provider.providerId))

        if (provider) {
          return provider
        } else {
          logException(new Error('Failed to get signin providers for email'), email)
          throw new Error('Something went wrong. If the problem persists, please contact support@thingtesting.com')
        }
      },

      signOut: async () => {
        trackAuthEvent('signOut')
        try {
          await push('/')
          const auth = getAuth()
          await signOut(auth)
          deleteAuthCookies()
          deleteKnockCookie()
          await client.cache.reset()
          addNotification('success', 'You have been logged out')
        } catch (err) {
          logException(err)
          trackAuthEvent('signOutError', undefined, { errorCode: err.code })
          throw err
        }
      },

      signInWithEmailAndPassword: async (ctx) => {
        try {
          trackAuthEvent('signInWithEmailAndPasswordStarted', ctx, { providerId: 'email' })
          const auth = getAuth()
          const result = await signInWithEmailAndPassword(auth, ctx.email, ctx.password)
          const additionalUserInfo = getAdditionalUserInfo(result)
          trackAuthEvent('signInWithEmailAndPasswordSuccess', ctx, { providerId: 'email' })
          return additionalUserInfo
        } catch (err) {
          trackAuthEvent('signInWithEmailAndPasswordError', ctx, { providerId: 'email', errorCode: err.code })
          switch (err.code) {
            case 'auth/wrong-password':
              throw new Error('Invalid password')
            case 'auth/user-not-found':
              throw new Error('The email address or password you have entered is incorrect')
            default:
              logException(err, ctx?.email)
              throw new Error('Something went wrong. If the problem persists, please contact support@thingtesting.com')
          }
        }
      },

      signInWithEmail: async (ctx) => {
        trackAuthEvent('signInWithEmailStarted', ctx, { providerId: 'email' })
        const { email } = ctx
        try {
          const auth = getAuth()
          await sendSignInLinkToEmail(auth, email, {
            url: `${window.location.origin}/auth/action`,
            handleCodeInApp: true
          })
          setLocalStorageItem(EMAIL_MEMO_KEY, String(email))
          window.analytics.track('User Started Sign In', { email }) // Here for legacy reasons
          trackAuthEvent('signInWithEmailSuccess', ctx, { providerId: 'email' })
        } catch (err) {
          trackAuthEvent('signInWithEmailError', ctx, { providerId: 'email', errorCode: err.code })
          switch (err.code) {
            case 'auth/invalid-email':
              throw new Error('The email address you have entered is invalid')

            case 'auth/user-disabled':
              throw new Error('Your account has been suspended and you can not continue.')

            default:
              logException(err, ctx?.email)
              throw new Error('Something went wrong. If the problem persists, please contact support@thingtesting.com')
          }
        }
      },

      completeEmailSignIn: async (ctx) => {
        trackAuthEvent('completeEmailSignInStarted', ctx, { providerId: 'email' })
        const auth = getAuth()
        if (isSignInWithEmailLink(auth, window.location.href)) {
          let email = getLocalStorageItem(EMAIL_MEMO_KEY)
          if (!email) {
            email = window.prompt('Please provide your email for confirmation')
          }

          try {
            const result = await signInWithEmailLink(auth, email, window.location.href)
            const additionalUserInfo = getAdditionalUserInfo(result)
            removeLocalStorageItem(EMAIL_MEMO_KEY)
            trackAuthEvent('completeEmailSignInSuccess', ctx, { providerId: 'email', isNewUser: additionalUserInfo.isNewUser })

            return additionalUserInfo
          } catch (err) {
            trackAuthEvent('completeEmailSignInError', ctx, { providerId: 'email', errorCode: err.code })
            switch (err.code) {
              case 'auth/expired-action-code':
                throw new Error('The link you used to sign in has expired. Please try logging in again.')

              case 'auth/invalid-email':
                throw new Error('The email address you have entered is invalid.')

              case 'auth/user-disabled':
                throw new Error('Your account has been suspended and you can not continue.')

              default:
                logException(err, ctx?.email)
                throw new Error('Something went wrong. If the problem persists, please contact support@thingtesting.com')
            }
          }
        } else {
          trackAuthEvent('completeEmailSignInError', ctx, { providerId: 'email', errorCode: 'invalid-link' })
          throw new Error('The link you have used to sign in is invalid. Please try logging in again.')
        }
      },

      /**
       * Once a user has sucessfully signed in or registered, wait for the initial 'me' query from
       * the session machine to complete.
       *
       * Then, if they're a new user, and the additionalUserInfo contains useful profile deets, use it to
       * update their profile. Finally, either invoke the onSigninSuccess function or redirect to onboarding
       * or home depending on if the user is new or not.
       */
      runSuccessfulSigninCallbacks: async (ctx) => {
        const { additionalUserInfo, onSigninSuccess } = ctx

        while (!userIsLoaded()) {
          await sleep(100)
        }

        if (additionalUserInfo?.isNewUser) {
          window.analytics.track('User Signed Up', getTrackingPayload(ctx))

          try {
            const profileData = mapProviderDataToProfileInput(additionalUserInfo)
            if (profileData) {
              await updateProfile({ variables: { input: profileData } })
            }
          } catch (err: any) {
            trackAuthEvent('updateProfileError', ctx, { error: err.message })
          }
        }

        addNotification('success', 'Signed in')

        if (typeof onSigninSuccess === 'function') {
          onSigninSuccess(ctx)
        } else if (onSigninSuccess === 'onboardingOrHome') {
          if (ctx.additionalUserInfo?.isNewUser) {
            returnToOrRedirect('/onboarding')
          } else {
            returnToOrRedirect('/')
          }
        }
      }
    }
  })

  return <AuthenticationContext.Provider value={{ authenticationService }}>{children}</AuthenticationContext.Provider>
}
